import { useEffect, useState } from "react";
interface Permissions {
  showDashboard: boolean;
  showCalendar: boolean;
  showListingManagement: boolean;
  showReservations: boolean;
  showAccounting: boolean;
  showInbox: boolean;
  showLockManagement: boolean;
  showMessagManagement: boolean;
  showOwnerManagement: boolean;
  showSubscriptionManagement: boolean;
  showGuestManagement: boolean;
  showAutomation: boolean;
  showTeamManagement: boolean;
  showAppMarketplace: boolean;
  showControlCenter: boolean;
  showTaskManagement: boolean;
  showYourWebsite: boolean;
  showSettings: boolean;
}

interface RolePermission {
  permissionId: number;
  permissionTitleEn: string;
  permissionTitleAr: string;
  permissionName: string;
}

const permissionNameMap: Record<string, keyof Permissions> = {
  Dashboard: "showDashboard",
  Calendar: "showCalendar",
  ListingManagement: "showListingManagement",
  Reservations: "showReservations",
  Accounting: "showAccounting",
  Inbox: "showInbox",
  LockManagement: "showLockManagement",
  MessagManagement: "showMessagManagement",
  OwnerManagement: "showOwnerManagement",
  SubscriptionManagement: "showSubscriptionManagement",
  GuestManagement: "showGuestManagement",
  Automation: "showAutomation",
  TeamManagement: "showTeamManagement",
  AppMarketplace: "showAppMarketplace",
  ControlCenter: "showControlCenter",
  TaskManagement: "showTaskManagement",
  YourWebsite: "showYourWebsite",
  Settings: "showSettings",
};

const handleNavigation = (state = "") => {
  const rawPermissions = localStorage.getItem("rolePermissions");
  const permissions = rawPermissions ? JSON.parse(rawPermissions) : [];

  const has = (permName: string) =>
    permissions.some((p: any) => p.permissionName === permName);

  // Check for OwnerPortal permission first - these users should only see their portal
  if (has("OwnerPortal")) {
    return "/owner-portal";
  }

  const route = has("Dashboard")
    ? "/dashboard"
    : has("Calendar")
      ? "/calendar"
      : has("Accounting")
        ? "/finance/accounting"
        : has("Messaging")
          ? "/messaging"
          : has("LocksManagement")
            ? "/locks-management"
            : has("Inbox")
              ? "/inbox"
              : has("ListingManagement")
                ? "/listing-management"
                : has("OwnerManagement")
                  ? "/owner-management"
                  : has("Reservations")
                    ? "/reservations"
                    : has("GuestManagement")
                      ? "/guest-management"
                      : has("Automation")
                        ? "/automation"
                        : has("TeamManagement")
                          ? "/user-management"
                          : has("SubscriptionManagement")
                            ? "/manage-plan"
                            : has("AppMarketplace")
                              ? "/app-marketplace"
                              : has("ControlCenter")
                                ? "/control-center"
                                : has("TaskManagement")
                                  ? "/task-management"
                                  : has("Website")
                                    ? "/website-builder"
                                    : has("Settings")
                                      ? "/settings"
                                      : state ?? "/";

  return route;
};

const usePermissions = () => {
  const [permissions, setPermissions] = useState<Permissions>({
    showDashboard: false,
    showCalendar: false,
    showListingManagement: false,
    showReservations: false,
    showAccounting: false,
    showInbox: false,
    showLockManagement: false,
    showMessagManagement: false,
    showOwnerManagement: false,
    showSubscriptionManagement: false,
    showGuestManagement: false,
    showAutomation: false,
    showTeamManagement: false,
    showAppMarketplace: false,
    showControlCenter: false,
    showTaskManagement: false,
    showYourWebsite: false,
    showSettings: false,
  });

  useEffect(() => {
    const userPermissionsStr = localStorage.getItem("rolePermissions");
    if (userPermissionsStr) {
      const userPermissions: RolePermission[] = JSON.parse(userPermissionsStr);
      setPermissions((prevPermissions) => {
        const updatedPermissions = { ...prevPermissions };

        userPermissions.forEach((permission) => {
          const mappedKey = permissionNameMap[permission.permissionName];
          if (mappedKey) {
            updatedPermissions[mappedKey] = true;
          }
        });
        return updatedPermissions;
      });
    }
  }, [JSON.stringify(localStorage.getItem("rolePermissions"))]);

  return permissions;
};

const useSubPermissions = () => {
  const [subPermissions, setSubPermissions] = useState({
    // Reservations sub-permissions
    showReservationsManualbooking: false,

    // Calendar sub-permissions
    showCalendarEditPriceAvailability: false,
    showCalendarEditReservations: false,
    showCalendarManualBooking: false,
    showCalendarSendReservationDetails: false,
  });

  useEffect(() => {
    const rawPermissions = localStorage.getItem("rolePermissions");
    if (!rawPermissions) return;

    const permissions = JSON.parse(rawPermissions);

    const newSubPermissions = {
      showReservationsManualbooking: false,
      showCalendarEditPriceAvailability: false,
      showCalendarEditReservations: false,
      showCalendarManualBooking: false,
      showCalendarSendReservationDetails: false,
    };

    // Check Reservations SubPermissions
    const reservationsPermission = permissions.find(
      (item: any) => item.permissionName === "Reservations"
    );
    if (reservationsPermission?.subPermissions) {
      newSubPermissions.showReservationsManualbooking =
        reservationsPermission.subPermissions.some(
          (subItem: any) => subItem.permissionName === "ManualBooking"
        );
    }

    // Check Calendar permissions
    const calendarPermission = permissions.find(
      (item: any) => item.permissionName === "Calendar"
    );
    if (calendarPermission?.subPermissions) {
      newSubPermissions.showCalendarEditPriceAvailability =
        calendarPermission.subPermissions.some(
          (subItem: any) =>
            subItem.permissionName === "EditPriceAndAvailabitlty"
        );
      newSubPermissions.showCalendarEditReservations =
        calendarPermission.subPermissions.some(
          (subItem: any) => subItem.permissionName === "EditReservations"
        );
      newSubPermissions.showCalendarManualBooking =
        calendarPermission.subPermissions.some(
          (subItem: any) => subItem.permissionName === "ManualBooking"
        );
      newSubPermissions.showCalendarSendReservationDetails =
        calendarPermission.subPermissions.some(
          (subItem: any) => subItem.permissionName === "SendReservationDetails"
        );
    }

    setSubPermissions(newSubPermissions);
  }, [JSON.stringify(localStorage.getItem("rolePermissions"))]); // Compare stringified value

  return subPermissions;
};

export { usePermissions, handleNavigation, useSubPermissions };
